/**
 * 固定費の一覧について「**何を出すか**」だけを決める（M5-FIXED-COST / `docs/02` §6.1）。
 *
 * ★このファイルは **DOM を触らない**（`targets-view.ts` と同じ理由）。
 *   判断を描画関数の中に書くと、その判断を壊しても `tsc` もテストも落ちない。
 *
 * ## ★ここが担っている判断は3つ
 *
 * ① **「未入力」と「0円」を分ける。** `fixed_cost_monthly` に行が無い月と、
 *    0 と入れた月は別。画面に「0」と出した瞬間に同じに見える——
 *    そして C-01 は**行が無い科目を数えない**ので、「入れ忘れ」が静かに通る
 * ② **手入力できる科目とできない科目を分ける。** `source = DERIVED` は
 *    人件費・小口現金からの連携専用（§6.1）で、手で入れると二重計上になる
 * ③ **1件も無いときに黙らない。** 科目0件と読み込み失敗が画面で同じにならないように
 *    （`.loop/memory.md`「未紐付け・空の結果に、黙って空を返さない」）
 */
import type { FixedCost } from "./api.js";

/** 未入力のときに欄の隣へ出す言葉。★0 のときは出さない */
export const UNSET_NOTE = "未入力";

/** ★`alloc_method` は割る数と載せる日の**両方**を決める（`docs/02` §6.2 の表） */
export const ALLOC_LABEL: Record<FixedCost["allocMethod"], string> = {
  BUSINESS_DAY: "営業日割（営業日数で割り、営業日に載せる）",
  CALENDAR_DAY: "暦日割（暦日数で割り、全暦日に載せる）",
  NONE: "配賦なし（日額 0 のまま月額に残る）",
};

export interface FixedCostRowView {
  id: string;
  accountCode: string;
  name: string;
  allocLabel: string;
  /** 入力欄の初期値。★未入力は空文字（`0` を置かない） */
  value: string;
  /** ★未入力の科目にだけ付く。0 の科目には付かない */
  note: string | null;
  /** ★`false` なら月額を手入力できない（`source = DERIVED`） */
  editable: boolean;
  /** 手入力できない理由・停止中である旨。無ければ `null` */
  hint: string | null;
}

export function fixedCostRows(rows: readonly FixedCost[]): FixedCostRowView[] {
  return rows.map((r) => ({
    id: r.id,
    accountCode: r.accountCode,
    name: r.name,
    allocLabel: ALLOC_LABEL[r.allocMethod],
    value: r.amount ?? "",
    note: r.amount === null ? UNSET_NOTE : null,
    editable: r.source === "MANUAL",
    hint:
      r.source !== "MANUAL"
        ? "他の機能から自動で計上される科目です"
        : r.isActive
          ? null
          : "使わない設定の科目です",
  }));
}

/**
 * 見出しの下に出す1行。
 *
 * ★**「全部入っている」ときも黙らない。** 未入力0件で何も出さない作りにすると、
 *   「読み込めていない」と「全部入っている」が画面で同じになる。
 */
export function fixedCostSummary(rows: readonly FixedCostRowView[]): string {
  if (rows.length === 0) {
    return "固定費の科目がまだ1件も登録されていません。科目を登録するまで固定費は 0 円のままです";
  }
  /* ★手入力できない科目を分母から外す。入れられないものを「入れ忘れ」と数えない */
  const manual = rows.filter((r) => r.editable);
  const unset = manual.filter((r) => r.note !== null).length;
  if (manual.length === 0) return `${rows.length}件すべてが自動で計上される科目です`;
  if (unset === 0) return `${manual.length}件、すべて入力済みです`;
  return `${manual.length}件のうち ${unset}件が${UNSET_NOTE}です（0円と入力した科目は入力済みとして数えます）`;
}

/**
 * 入力欄の文字列を、API へ送る月額に変える。
 *
 * ★★**`Number("")` は 0。** 欄を空にしたまま保存すると、
 *   `ON CONFLICT DO UPDATE` が既に入っている月額を **0 で塗り替えていた**
 *   （2026-09-10・code 確認が実測した退行）。「未入力（`null`）と 0円 は別」という
 *   このファイルの① の原則を、画面の送信ハンドラ1本だけが破っていた。
 *
 * ★**空白だけの入力も同じ**（`Number("  ")` も 0）。`required` はここを素通りするので、
 *   ブラウザ側の `required` とは別に、この関数が最後の関門になる。
 * ★`null` を送るとサーバの `assertAmount` が「数値で入力してください」で断る。
 *   ★**ここで断らない。** 断り文言を2箇所に持つと片方だけ変わる。
 */
export function amountToSend(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : Number(trimmed);
}
